"use client";

import { motion } from "framer-motion";
import { ArrowDown, MapPin, Award, Sparkles, Target, Users2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import Image from "next/image";

const EnhancedAboutHero = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-background via-primary/5 to-accent/10" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-accent/20 via-transparent to-transparent" />
      
      {/* Floating Elements */}
      <motion.div
        className="absolute top-20 left-10 w-20 h-20 bg-accent/20 rounded-full blur-xl"
        animate={{
          y: [0, -20, 0],
          opacity: [0.3, 0.6, 0.3],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      <motion.div
        className="absolute bottom-20 right-10 w-32 h-32 bg-primary/10 rounded-full blur-xl"
        animate={{
          y: [0, 20, 0],
          opacity: [0.2, 0.5, 0.2],
        }}
        transition={{
          duration: 5,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1,
        }}
      />

      <div className="container max-w-7xl mx-auto px-6 py-20 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-accent/10 border border-accent/20 rounded-full text-sm font-medium"
            >
              <Sparkles className="w-4 h-4 text-accent" />
              Meet the Engineer Behind the Revolution
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight"
            >
              From Manual Chaos to{" "}
              <span className="text-gradient">AI-Powered Precision</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-xl md:text-2xl text-muted-foreground leading-relaxed"
            >
              I&apos;m Abdul Rehman, and I&apos;ve spent the last 7+ years watching brilliant teams 
              waste their potential on repetitive tasks. That&apos;s why I build AI systems 
              that give people their time back—and their companies a competitive edge.
            </motion.p>

            {/* Key Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="grid grid-cols-3 gap-6"
            >
              <div className="text-center">
                <div className="text-3xl font-bold text-accent">80+</div>
                <div className="text-sm text-muted-foreground">Workflows Automated</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-accent">250K+</div>
                <div className="text-sm text-muted-foreground">Hours Saved</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-accent">6.5×</div>
                <div className="text-sm text-muted-foreground">Average ROI</div>
              </div>
            </motion.div>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Button
                size="lg"
                className="group bg-accent hover:bg-accent/90 text-accent-foreground"
                onClick={() => document.getElementById('journey')?.scrollIntoView({ behavior: 'smooth' })}
              >
                <Target className="mr-2 w-5 h-5" />
                Discover My Journey
                <ArrowDown className="ml-2 w-4 h-4 group-hover:translate-y-1 transition-transform" />
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="group"
                onClick={() => document.getElementById('impact')?.scrollIntoView({ behavior: 'smooth' })}
              >
                <Users2 className="mr-2 w-5 h-5" />
                See the Impact
              </Button>
            </motion.div>
          </motion.div>

          {/* Right Column - Visual */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            {/* Profile Image with Enhanced Design */}
            <div className="relative w-full max-w-lg mx-auto">
              {/* Background Decorations */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-br from-accent/20 to-primary/20 rounded-3xl -rotate-6"
                animate={{ rotate: [-6, -4, -6] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              />
              <motion.div
                className="absolute inset-0 bg-gradient-to-tl from-primary/20 to-accent/20 rounded-3xl rotate-3"
                animate={{ rotate: [3, 5, 3] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              />
              
              {/* Main Image Container */}
              <div className="relative w-full aspect-square rounded-3xl overflow-hidden border-2 border-border bg-card shadow-2xl">
                <Image 
                  src="/rehman.webp" 
                  alt="Abdul Rehman - AI Automation Engineer" 
                  fill
                  className="object-cover"
                  priority
                />
                
                {/* Overlay with subtle gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-background/20 via-transparent to-transparent" />
              </div>

              {/* Floating Achievement Cards */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.8 }}
                className="absolute -top-4 -right-4 bg-card border border-border rounded-xl p-4 shadow-lg"
              >
                <div className="flex items-center gap-2">
                  <Award className="w-5 h-5 text-accent" />
                  <div>
                    <div className="text-sm font-semibold">7+ Years</div>
                    <div className="text-xs text-muted-foreground">AI Expertise</div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 1 }}
                className="absolute -bottom-4 -left-4 bg-card border border-border rounded-xl p-4 shadow-lg"
              >
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-accent" />
                  <div>
                    <div className="text-sm font-semibold">Global</div>
                    <div className="text-xs text-muted-foreground">Remote Work</div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Philosophy Quote */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-20 text-center"
        >
          <div className="max-w-4xl mx-auto bg-gradient-to-r from-accent/10 via-primary/5 to-accent/10 rounded-3xl p-8 border border-accent/20">
            <blockquote className="text-2xl md:text-3xl font-bold mb-4">
              &ldquo;Technology should amplify human potential, not replace it.&rdquo;
            </blockquote>
            <p className="text-lg text-muted-foreground">
              This philosophy drives every AI system I build—creating solutions that handle the mundane 
              so humans can focus on the meaningful.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default EnhancedAboutHero; 